import { TicketCategory, TicketPriority, TicketStatus } from "@signalist/shared-types";

import { GetStaffTicketsArg } from "@/pages/Private/redux/tickets/tickets-admin.params";

import { STAFF_TAB_PRESETS, StaffTicketTab, TabAssigneeRule } from "./staffTicketTabs";

/** Anyone, nobody, or one staff member's id. */
export type AssigneeFilter = "ANY" | "UNASSIGNED" | number;

export interface StaffQueueFilters {
	/** A subset of the active tab's statuses; empty = the whole tab. */
	statuses: TicketStatus[];
	assignee: AssigneeFilter;
	category: TicketCategory | null;
	priority: TicketPriority | null;
	companyId: number | null;
	/** Known when picked in the UI; null when seeded from `?companyId=` (looked up separately). */
	companyName: string | null;
}

export const EMPTY_STAFF_FILTERS: StaffQueueFilters = {
	statuses: [],
	assignee: "ANY",
	category: null,
	priority: null,
	companyId: null,
	companyName: null,
};

export interface StaffQueueInput {
	tab: StaffTicketTab;
	filters: StaffQueueFilters;
	/** The debounced search term. */
	search: string;
	page: number;
	limit: number;
	currentUserId: number | null;
}

/** The tab's statuses narrowed by the selection; a selection outside the tab counts for nothing. */
export const effectiveStatuses = (
	tab: StaffTicketTab,
	selected: TicketStatus[]
): TicketStatus[] => {
	const preset = STAFF_TAB_PRESETS[tab].statuses;
	const picked = preset.filter((status) => selected.includes(status));

	return picked.length ? picked : preset;
};

const assigneeArg = (
	rule: TabAssigneeRule,
	filter: AssigneeFilter,
	currentUserId: number | null
): Pick<GetStaffTicketsArg, "assigneeUserId" | "unassigned"> => {
	if (rule === "MINE") {
		return currentUserId ? { assigneeUserId: currentUserId } : {};
	}

	if (rule === "UNASSIGNED" || filter === "UNASSIGNED") {
		return { unassigned: true };
	}

	return typeof filter === "number" ? { assigneeUserId: filter } : {};
};

/**
 * Tab + filter row + paging → the exact `GET tickets/admin` arg (story AC: tabs and filters map to
 * the query parameters). The tab's assignee rule wins over the filter row on Mine / Unassigned.
 */
export const buildStaffQueueArg = ({
	tab,
	filters,
	search,
	page,
	limit,
	currentUserId,
}: StaffQueueInput): GetStaffTicketsArg => {
	const term = search.trim();

	return {
		page,
		limit,
		status: effectiveStatuses(tab, filters.statuses),
		...assigneeArg(STAFF_TAB_PRESETS[tab].assignee, filters.assignee, currentUserId),
		...(filters.category ? { category: filters.category } : {}),
		...(filters.priority ? { priority: filters.priority } : {}),
		...(filters.companyId ? { companyId: filters.companyId } : {}),
		...(term ? { search: term } : {}),
	};
};

export const toggleStatus = (selected: TicketStatus[], status: TicketStatus): TicketStatus[] =>
	selected.includes(status) ? selected.filter((item) => item !== status) : [...selected, status];

/**
 * Value equality for the filter row. The shared `Dropdown` re-selects its `value` from an effect on
 * every render, so a host that always stores a new object re-renders forever; the hook drops a
 * patch that changes nothing.
 */
export const staffFiltersEqual = (a: StaffQueueFilters, b: StaffQueueFilters): boolean =>
	a.statuses.length === b.statuses.length &&
	a.statuses.every((status, index) => status === b.statuses[index]) &&
	a.assignee === b.assignee &&
	a.category === b.category &&
	a.priority === b.priority &&
	a.companyId === b.companyId &&
	a.companyName === b.companyName;
